import {Group, Vector2, Vector4, RGBFormat, LinearFilter, SRGBColorSpace, PlaneGeometry, ShaderMaterial, Mesh } from 'three'
import Experience from "../Experience.js";
import fragmentShader from "../../shaders/hero/fragment.glsl";
import vertexShader from "../../shaders/hero/vertex.glsl";

export default class HeroWall {

    static _instance = null;
    gallery = new Group();

    constructor(fluidCursorTexture) {

        if ( HeroWall._instance ) {
            return HeroWall._instance
        }

        HeroWall._instance = this

        this.experience = new Experience()
        this.input = this.experience.input
        this.sizes = this.experience.sizes
        this.resources = this.experience.resources
        this.scene = this.experience.scene
        this.fluidCursorTexture = fluidCursorTexture
        this.pano = this.experience.pano
        this.canvas = this.experience.renderer.canvas
        this.init()
        this.initEvents()
    }

    static getInstance() {
        return HeroWall._instance || new HeroWall()
    }

    initTexture() {
        if(this.resources.items.videoTextureHero !== null) {
            this.videoTexture = this.resources.items.videoTextureHero

            this.videoTexture.videoTexture.format = RGBFormat;
            this.videoTexture.videoTexture.colorSpace = SRGBColorSpace;
            this.videoTexture.videoTexture.minFilter = LinearFilter
            this.videoTexture.videoTexture.magFilter = LinearFilter
            this.videoTexture.videoTexture.generateMipmaps = false

            this.videoElement = this.videoTexture.videoElement
            this.videoElement.play()

            const resolution = this.calcAspectRatio()

            this.material.uniforms.uVideo.value = this.videoTexture.videoTexture
            this.material.uniforms.uResolution.value = new Vector4(resolution.x, resolution.y, resolution.z, resolution.w)
        } else {
            this.videoTexture = null;
        }
    }

    init() {
        this.scene.add(this.gallery)

        this.geometry = new PlaneGeometry(2, 2)
        this.material = new ShaderMaterial({
            uniforms: {
                uVideo: { value: null },
                uDisplacement: { value: this.fluidCursorTexture },
                uDisplacementFactor: { value: 0.3 },
                uResolution: { value: new Vector4() },
                uGrainAmount: { value: 0.035 },
                uGrainSize: { value: this.sizes.isMobile ? .8 : 1.4 },
                uLumAmount: { value: 1.0 },
                uTimer: { value: 0.0 },
                uCursorDelta: { value: new Vector2() },
            },
            vertexShader: vertexShader,
            fragmentShader: fragmentShader
        });

        this.mesh = new Mesh(this.geometry, this.material);
        // this.fitPlaneToCamera();
        this.gallery.add(this.mesh);
    }

    fitPlaneToCamera() {
        const distance = this.experience.camera.instance.position.z
        const aspect = this.sizes.hero.width / this.sizes.hero.height

        const vFov = this.experience.camera.instance.fov * Math.PI / 180
        const planeHeightAtDistance = 2 * Math.tan(vFov / 2) * distance
        const planeWidthAtDistance = planeHeightAtDistance * aspect

        this.mesh.scale.x = planeWidthAtDistance
        this.mesh.scale.y = planeHeightAtDistance
    }

    initEvents() {
        this.resources.on('ready', () => {
            this.initTexture()
        })
    }

    calcAspectRatio() {
        const videoSizes = [
            { width: 512, height: 512 },
            { width: 1920, height: 1080 }
        ]

        const sizesIndex = this.sizes.isMobile ? 0 : 1

        const videoWidth = videoSizes[sizesIndex].width;
        const videoHeight = videoSizes[sizesIndex].height;
        const videoAspect = videoHeight / videoWidth;

        let a1;
        let a2;
        if (this.sizes.hero.height / this.sizes.hero.width > videoAspect) {
            a1 = (this.sizes.hero.width / this.sizes.hero.height) * videoAspect;
            a2 = 1;
        } else {
            a1 = 1;
            a2 = this.sizes.hero.height / this.sizes.hero.width / videoAspect;
        }

        const x = this.sizes.hero.width;
        const y = this.sizes.hero.height;
        const z = a1;
        const w = a2;
        return { x, y, z, w }
    }

    updateGlobalCursor() {
        const rect = this.canvas.getBoundingClientRect()
        const cursorDelta = this.input.calculateCursorDelta(rect)
        this.material.uniforms.uCursorDelta.value.copy(cursorDelta)
    }
    update(deltaTime) {
        if(this.videoTexture && this.videoTexture.videoTexture) {
            this.videoTexture.videoTexture.update()
        }

        this.updateGlobalCursor()

        this.material.uniforms.uTimer.value += deltaTime
    }

    resize() {
        const resolution = this.calcAspectRatio()
        this.material.uniforms.uResolution.value.set(resolution.x, resolution.y, resolution.z, resolution.w)
        // this.fitPlaneToCamera()
    }

    destroy() {
        if (this.scene) {
            this.scene.remove(this.gallery);
        }

        if (this.geometry) {
            this.geometry.dispose();
        }
        if (this.material) {
            this.material.dispose();
        }
        if (this.mesh) {
            this.mesh.geometry.dispose();
            this.mesh.material.dispose();
        }

        if (this.videoTexture) {
            this.videoTexture.videoTexture.dispose();
        }
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.src = '';
            this.videoElement.load();
        }

        if (this.resources) {
            this.resources.off('ready', this.initTexture);
        }

        this.gallery = null;
        this.experience = null;
        this.input = null;
        this.sizes = null;
        this.resources = null;
        this.scene = null;
        this.fluidCursorTexture = null;
        this.pano = null;
        this.canvas = null;
        this.geometry = null;
        this.material = null;
        this.mesh = null;
        this.videoTexture = null;
        this.videoElement = null;

        HeroWall._instance = null;
    }
}
